/**
 * Job Action Handlers Hook
 *
 * Provides bulk action handlers for job operations including:
 * - Rerunning selected jobs
 * - Canceling selected jobs
 *
 * Risk Level: Medium
 * - Async operations
 * - Multiple external dependencies
 * - Requires coordination with job mutations
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { useCancelJob, useReRunJob } from '@/hooks/useJobs';
import {
  aggregatePromiseResults,
  buildBulkActionMessage,
} from '@/sections/jobs/utils/jobMessages.util';
import type { Job } from '@/types';

/**
 * Parameters for useJobsActionHandlers hook
 */
interface UseJobsActionHandlersParams {
  /** Currently selected jobs */
  selectedJobs: Job[];
  /** Function to invalidate/refetch job data */
  refetch: () => void;
}

/**
 * Return type for useJobsActionHandlers hook
 */
interface JobsActionHandlersReturn {
  /** Handler for rerunning jobs */
  handleRerun: (jobs: Job[]) => Promise<void>;
  /** Handler for canceling jobs */
  handleCancel: (jobs: Job[]) => Promise<void>;
}

/**
 * Hook to manage job bulk action handlers
 *
 * Provides handlers for:
 * - Rerunning selected jobs in parallel
 * - Canceling selected jobs in parallel
 * - Accurate success/failure feedback with toast notifications
 *
 * @param params - Configuration object with dependencies
 * @returns Object containing action handlers
 *
 * @example
 * ```tsx
 * const handlers = useJobsActionHandlers({
 *   selectedJobs,
 *   refetch,
 * });
 *
 * // Use in keyboard shortcuts or bulk actions
 * await handlers.handleRerun(selectedJobs);
 * await handlers.handleCancel(selectedJobs);
 * ```
 */
export const useJobsActionHandlers = ({
  selectedJobs: _selectedJobs,
  refetch,
}: UseJobsActionHandlersParams): JobsActionHandlersReturn => {
  // Get mutation functions for job operations
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();

  /**
   * Handles rerunning selected jobs
   *
   * Reruns all selected jobs in parallel, handles partial failures
   * gracefully, and shows accurate feedback.
   *
   * Uses Promise.allSettled to ensure all operations complete even if some fail,
   * providing accurate user feedback about successes and failures.
   *
   * @param jobs - Array of jobs to rerun
   */
  const handleRerun = useCallback(
    async (jobs: Job[]) => {
      if (jobs.length === 0) {
        toast.warning('No jobs selected');
        return;
      }

      const promises = jobs.map(job => {
        return reRunJob({ jobKey: job.key });
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures using extracted utility
      const { succeeded, failed } = aggregatePromiseResults(results);

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        refetch();
      }

      // Show accurate feedback based on results using extracted utility
      const msg = buildBulkActionMessage('reran', succeeded, failed);

      if (msg.type === 'success') {
        toast.success(msg.message);
      } else if (msg.type === 'partial') {
        toast.warning(msg.message);
      } else {
        toast.error(msg.message);
      }

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            job: jobs[i]?.key,
            error: r.reason,
          }));
        console.error(
          '[useJobsActionHandlers] Failed to rerun jobs:',
          failedResults
        );
      }
    },
    [reRunJob, refetch]
  );

  /**
   * Handles canceling selected jobs
   *
   * Cancels all selected jobs in parallel, handles partial failures
   * gracefully, and shows accurate feedback.
   *
   * Uses Promise.allSettled to ensure all operations complete even if some fail,
   * providing accurate user feedback about successes and failures.
   *
   * @param jobs - Array of jobs to cancel
   */
  const handleCancel = useCallback(
    async (jobs: Job[]) => {
      if (jobs.length === 0) {
        toast.warning('No jobs selected');
        return;
      }

      const promises = jobs.map(job => {
        return cancelJob({ jobKey: job.key });
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures using extracted utility
      const { succeeded, failed } = aggregatePromiseResults(results);

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        refetch();
      }

      // Show accurate feedback based on results using extracted utility
      const msg = buildBulkActionMessage('canceled', succeeded, failed);

      if (msg.type === 'success') {
        toast.success(msg.message);
      } else if (msg.type === 'partial') {
        toast.warning(msg.message);
      } else {
        toast.error(msg.message);
      }

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            job: jobs[i]?.key,
            error: r.reason,
          }));
        console.error(
          '[useJobsActionHandlers] Failed to cancel jobs:',
          failedResults
        );
      }
    },
    [cancelJob, refetch]
  );

  return {
    handleRerun,
    handleCancel,
  };
};
